// Turns a captured Cisco inventory into a first-draft architecture diagram.
//
// An inventory is a bill of materials, not a topology — it says "you own
// Catalyst access switches and a Secure Firewall", not how they're cabled. So
// this derivation places every captured item into the architectural tier that
// product family conventionally lives in, then wires each tier to the one above
// it in the obvious spine pattern.
//
// SELF-CONTAINED port from the example app's modules/topology/build.ts. The
// `EntitySelection` / `UseCaseSelection` input shapes are inlined minimally.

import { getEntityItem } from "../catalog/entityCatalog";
import {
  TOPO_MODEL_VERSION,
  TOPO_TIERS,
  edgeId,
  type TopoEdge,
  type TopoEdgeKind,
  type TopoModel,
  type TopoNode,
  type TopoNodeKind,
  type TopoTierId,
} from "./topologyTypes";

/** Minimal committed-entity shape `buildTopology` reads. */
export type EntitySelection = {
  itemId: string;
};

/** Minimal use-case shape `buildTopology` reads. Only `env.cloud_onprem_mix`
 *  and `env.ai_agents_deployed` are consulted. */
export type UseCaseSelection = {
  id: string;
  env?: Record<string, any>;
};

type Placement = { kind: TopoNodeKind; tier: TopoTierId };

/** Item-level placement. Anything not listed falls back to its category. */
const ITEM_PLACEMENT: Record<string, Placement> = {
  // Cisco Compute
  hyper_converged: { kind: "server", tier: "dc" },
  intersight: { kind: "cloud_mgmt", tier: "ops" },
  servers: { kind: "server", tier: "dc" },

  // Collaboration
  cloud_contact_center: { kind: "cloud_app", tier: "cloud" },
  cloud_webex_suite: { kind: "cloud_app", tier: "cloud" },
  cpaas: { kind: "cloud_app", tier: "cloud" },
  onprem_contact_center: { kind: "server", tier: "dc" },
  onprem_webex_suite: { kind: "server", tier: "dc" },
  phone_headset_devices: { kind: "phone", tier: "endpoint" },
  video_devices: { kind: "video", tier: "endpoint" },

  // Data Center Networking
  nx1k: { kind: "dc_switch", tier: "dc" },
  nx2_4_5_6k: { kind: "dc_switch", tier: "dc" },
  nx3k: { kind: "dc_switch", tier: "dc" },
  nx7k: { kind: "dc_switch", tier: "dc" },
  nx9k: { kind: "dc_switch", tier: "dc" },
  optics_dc_allocation: { kind: "optics", tier: "dc" },
  san: { kind: "storage", tier: "dc" },

  // Enterprise Routing
  ent_access_routing: { kind: "router", tier: "edge" },
  ent_edge_routing: { kind: "router", tier: "edge" },
  iiot_routing: { kind: "iot_router", tier: "access" },
  meraki_sdwan: { kind: "sdwan_edge", tier: "edge" },

  // Enterprise Switching
  ent_switching_other: { kind: "switch", tier: "access" },
  ent_sb_switching: { kind: "switch", tier: "access" },
  ent_switching_access: { kind: "switch", tier: "access" },
  ent_switching_connected_platforms: { kind: "switch", tier: "access" },
  ent_switching_core: { kind: "l3_switch", tier: "core" },
  iiot_switching: { kind: "iot_switch", tier: "access" },
  meraki_switching: { kind: "switch", tier: "access" },
  optics_ent_networking_allocation: { kind: "optics", tier: "core" },

  // Internet Infrastructure (SP / carrier)
  sp_automation: { kind: "cloud_mgmt", tier: "ops" },
  sp_control_center: { kind: "cloud_mgmt", tier: "ops" },
  sp_mobility: { kind: "sp_router", tier: "cloud" },
  sp_optical: { kind: "optics", tier: "cloud" },
  sp_optics_routing_allocation: { kind: "optics", tier: "cloud" },
  sp_access_aggregation: { kind: "sp_router", tier: "cloud" },
  sp_core_routing: { kind: "sp_router", tier: "cloud" },
  sp_edge_routing: { kind: "sp_router", tier: "cloud" },
  sp_network_assurance: { kind: "monitor", tier: "ops" },

  // Network Assurance
  thousandeyes: { kind: "monitor", tier: "ops" },

  // Other
  data_analytics: { kind: "analytics", tier: "ops" },
  other_other: { kind: "generic", tier: "endpoint" },

  // Other Enterprise Networking
  meraki_iot: { kind: "iot", tier: "endpoint" },
  meraki_vision: { kind: "camera", tier: "endpoint" },

  // Security
  iam: { kind: "identity", tier: "edge" },
  network_security: { kind: "firewall", tier: "edge" },
  secure_service_edge: { kind: "sase", tier: "cloud" },
  threat_intel_detection_response: { kind: "shield", tier: "ops" },

  // Observability
  splunk: { kind: "analytics", tier: "ops" },
  appdynamics: { kind: "analytics", tier: "ops" },

  // Wireless
  cisco_spaces: { kind: "cloud_app", tier: "cloud" },
  ent_wireless: { kind: "wlc", tier: "core" },
  meraki_wireless: { kind: "ap", tier: "access" },
};

const CATEGORY_PLACEMENT: Record<string, Placement> = {
  cisco_compute: { kind: "server", tier: "dc" },
  collaboration: { kind: "phone", tier: "endpoint" },
  data_center_networking: { kind: "dc_switch", tier: "dc" },
  enterprise_routing: { kind: "router", tier: "edge" },
  enterprise_switching: { kind: "switch", tier: "access" },
  internet_infrastructure: { kind: "sp_router", tier: "cloud" },
  network_assurance: { kind: "monitor", tier: "ops" },
  observability: { kind: "analytics", tier: "ops" },
  other: { kind: "generic", tier: "endpoint" },
  other_enterprise_networking: { kind: "iot", tier: "endpoint" },
  security: { kind: "shield", tier: "edge" },
  wireless: { kind: "ap", tier: "access" },
};

const CLOUD_RANK: Record<string, number> = {
  all_on_prem: 0,
  hybrid: 1,
  mostly_cloud: 2,
  multi_cloud: 3,
};
const AI_RANK: Record<string, number> = { none: 0, pilot: 1, production: 2 };

const CLOUD_LABEL: Record<string, string> = {
  hybrid: "Public cloud (hybrid)",
  mostly_cloud: "Public cloud",
  multi_cloud: "Multi-cloud (2+ providers)",
};
const AI_LABEL: Record<string, string> = {
  pilot: "AI agents (pilot)",
  production: "AI agents (production)",
};

/** How many nodes sit in one isometric band before a tier wraps onto a
 *  second band. Six keeps the widest tier readable without horizontal
 *  scrolling on a laptop. */
const MAX_PER_BAND = 6;

function peak(values: (string | undefined)[], rank: Record<string, number>): string | undefined {
  let best: string | undefined;
  for (const v of values) {
    if (!v || !(v in rank)) continue;
    if (best === undefined || rank[v] > rank[best]) best = v;
  }
  return best;
}

export type BuildTopologyInput = {
  entities: EntitySelection[];
  useCases: UseCaseSelection[];
};

/**
 * Deterministic: the same inventory always produces the same diagram, so a
 * user who re-runs the assessment doesn't get their layout reshuffled.
 */
export function buildTopology({ entities, useCases }: BuildTopologyInput): TopoModel {
  const drafts: Omit<TopoNode, "col" | "row">[] = [];

  // 1. Synthetic anchors that every enterprise diagram needs but no
  //    inventory line item will ever produce.
  drafts.push({ id: "syn_internet", label: "Internet", kind: "internet", tier: "cloud" });

  const cloudMix = peak(useCases.map((u) => u.env?.cloud_onprem_mix), CLOUD_RANK);
  if (cloudMix && cloudMix !== "all_on_prem") {
    drafts.push({
      id: "syn_cloud",
      label: CLOUD_LABEL[cloudMix] ?? "Public cloud",
      kind: "cloud",
      tier: "cloud",
      note: "Derived from your cloud / on-prem answer.",
    });
  }

  // 2. One node per captured inventory item.
  for (const selection of entities) {
    const item = getEntityItem(selection.itemId);
    if (!item) continue;
    const placement =
      ITEM_PLACEMENT[item.id] ?? CATEGORY_PLACEMENT[item.categoryId] ?? { kind: "generic" as const, tier: "endpoint" as const };
    drafts.push({
      id: `ent_${item.id}`,
      label: item.label,
      kind: placement.kind,
      tier: placement.tier,
      sourceItemIds: [item.id],
      legacy: item.eolRisk,
    });
  }

  // 3. AI agents are an architectural element in this model, not an
  //    attribute — the whole framework is about what happens when they get
  //    a foothold, so they belong on the picture.
  const ai = peak(useCases.map((u) => u.env?.ai_agents_deployed), AI_RANK);
  if (ai && ai !== "none") {
    drafts.push({
      id: "syn_ai_agents",
      label: AI_LABEL[ai] ?? "AI agents",
      kind: "ai_agent",
      tier: "endpoint",
      note: "Derived from your AI-agent deployment answer.",
    });
  }

  drafts.push({ id: "syn_users", label: "Users & workstations", kind: "generic", tier: "endpoint" });

  const nodes = layout(drafts);
  return { version: TOPO_MODEL_VERSION, nodes, edges: autoWire(nodes) };
}

/** Assigns isometric grid coordinates: tiers become bands running from the
 *  north-west corner down, wrapping when a tier is wider than `MAX_PER_BAND`. */
export function layout(drafts: Omit<TopoNode, "col" | "row">[]): TopoNode[] {
  const out: TopoNode[] = [];
  let band = 0;
  for (const tier of TOPO_TIERS) {
    const inTier = drafts.filter((d) => d.tier === tier.id);
    if (inTier.length === 0) continue;
    for (let i = 0; i < inTier.length; i++) {
      const bandOffset = Math.floor(i / MAX_PER_BAND);
      out.push({ ...inTier[i], col: i % MAX_PER_BAND, row: band + bandOffset });
    }
    band += Math.ceil(inTier.length / MAX_PER_BAND);
  }
  return out;
}

/** Re-runs `layout` over an existing model's nodes — the "tidy up" action
 *  after a user has dragged things into a mess. */
export function relayout(model: TopoModel): TopoModel {
  return { ...model, nodes: layout(model.nodes) };
}

// Which tier each tier hangs off. This is an explicit map rather than a walk
// down an ordered list, because the ordered-list version wired access
// switches to *servers* and then added a second core→access link on top, so
// the picture showed two different stories at once.
const PARENT_TIER: Partial<Record<TopoTierId, TopoTierId>> = {
  edge: "cloud",
  core: "edge",
  dc: "core",
  access: "core",
  endpoint: "access",
};

const EDGE_KIND: Record<string, TopoEdgeKind> = {
  "cloud>edge": "wan",
  "edge>core": "uplink",
  "core>dc": "uplink",
  "core>access": "trunk",
  "access>endpoint": "trunk",
};

/** The node a tier aggregates onto. Deterministic (first node in the tier)
 *  rather than round-robin: an inventory cannot know which of three core
 *  switches a given access switch is actually cabled to. */
function anchorOf(byTier: Map<TopoTierId, TopoNode[]>, tier: TopoTierId): TopoNode | undefined {
  return byTier.get(tier)?.[0];
}

/** Walks up the parent chain until it finds a tier that actually has nodes,
 *  so an estate with no core still connects access to the perimeter rather
 *  than leaving it stranded. */
function resolveParent(
  byTier: Map<TopoTierId, TopoNode[]>,
  tier: TopoTierId,
): { node: TopoNode; tier: TopoTierId } | undefined {
  let cursor = PARENT_TIER[tier];
  const seen = new Set<TopoTierId>();
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const node = anchorOf(byTier, cursor);
    if (node) return { node, tier: cursor };
    cursor = PARENT_TIER[cursor];
  }
  return undefined;
}

/** Derives the links.
 *
 *  Deliberately sparse. What is drawn is only what can be inferred: the spine
 *  between tiers. One link per tier boundary, anchored on each tier's first
 *  component. An unconnected component is not a claim that it is unconnected in
 *  reality — it is the absence of a claim. */
export function autoWire(nodes: TopoNode[]): TopoEdge[] {
  const byTier = new Map<TopoTierId, TopoNode[]>();
  for (const n of nodes) byTier.set(n.tier, [...(byTier.get(n.tier) ?? []), n]);

  const edges: TopoEdge[] = [];
  const push = (from: string, to: string, kind: TopoEdgeKind) => {
    const id = edgeId(from, to);
    if (from !== to && !edges.some((e) => e.id === id)) edges.push({ id, from, to, kind });
  };

  for (const tier of ["edge", "core", "dc", "access", "endpoint"] as TopoTierId[]) {
    const anchor = anchorOf(byTier, tier);
    if (!anchor) continue;
    const parent = resolveParent(byTier, tier);
    if (!parent) continue;
    push(parent.node.id, anchor.id, EDGE_KIND[`${parent.tier}>${tier}`] ?? "uplink");
  }

  const opsAnchor =
    anchorOf(byTier, "core") ?? anchorOf(byTier, "edge") ?? anchorOf(byTier, "dc");
  const opsTool = anchorOf(byTier, "ops");
  if (opsAnchor && opsTool) push(opsTool.id, opsAnchor.id, "mgmt");

  return edges;
}
